import {
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    Optional,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    inject
} from "@angular/core";
import { UntypedFormGroup, FormsModule, ReactiveFormsModule, FormGroup } from "@angular/forms";

import { EMPTY, from, of } from "rxjs";
import { catchError, finalize, first, switchMap, tap } from "rxjs/operators";

import { DffError, isNumeric } from "@dffedb/util";
import { AuthenticationResult, LoginService } from "@e-forsyning/common/auth";
import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "@e-forsyning/common/app-shell";

import { LoginData } from "../login-data.model";
import { LoginHjaelpService } from "../login-hjaelp/login-hjaelp.service";
import { resolveTeknikerLoginMatomoChannel } from "../login-matomo.util";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatButton } from "@angular/material/button";
import { MatomoTracker } from "ngx-matomo-client";
import { LocalStorage } from "@dffedb/util";
import { MatExpansionPanel } from "@angular/material/expansion";
import { RedirectService } from "@e-forsyning/common/router";
import { DffRouteParams } from "@dffedb/router";
import { CommonModule } from "@angular/common";
import { BiometricService } from "../biometrics/biometrics.service";

@Component({
    selector: "login-input",
    templateUrl: "./login-input.html",
    styleUrl: "./login-input.scss",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatCheckbox, MatButton, MatExpansionPanel]
})
export class LoginInput implements OnChanges {
    @Input()
    public parent: UntypedFormGroup;

    @Input() public technicianLogin: boolean = false;

    @Input() public loginDisabled: boolean = false;

    @Input() public technicianContactPhone?: string;

    @Input() public emailPlaceholder?: string;

    @Input() public passwordPlaceholder?: string;

    @Input() public helpLinkText?: string;

    @Input() public showHelpLink: boolean = true;

    @Input() public forgotPasswordText?: string;

    @Input() public firstTimeText?: string;

    @Input() public showFirstTimeLink: boolean = true;

    @Output()
    public loginSucceeded = new EventEmitter<any>();

    @Output()
    public alphaKeyPressed = new EventEmitter<string>();

    @Output()
    public isLoading = new EventEmitter<boolean>();

    @ViewChild("forbrugerNrInput", { static: true }) public forbrugerNrInput: ElementRef;
    @ViewChild("emailInput") private emailInput: ElementRef<HTMLInputElement>;

    @ContentChild("biometricLoginButton") biometricTemplate?: TemplateRef<any>;

    public foersteGangAktiveret = false;
    public glemtKodeordAktiveret = false;
    public arbejder = false;
    public infoBesked = "";
    public fejlBesked = "";
    public visFeltFejl = false;

    private readonly matomoTracker = inject(MatomoTracker);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    constructor(
        private readonly loginService: LoginService,
        private readonly loginHjaelpService: LoginHjaelpService,
        private readonly localStorage: LocalStorage,
        private readonly redirectService: RedirectService,
        private readonly routeParams: DffRouteParams,
        private readonly biometricService: BiometricService,
        @Optional() @Inject(APP_SHELL_LAYOUT_OPTIONS) private readonly appShellLayout: AppShellLayoutOptions | null
    ) {}

    /** Matomo name: Tekniker-login (eForsyning) vs ServicePro-login. */
    private get teknikerLoginMatomoChannel(): string {
        return resolveTeknikerLoginMatomoChannel(this.appShellLayout?.teknikerLoginRoute);
    }

    ngOnInit(): void {
        const grp = this.parent.get("forbrugerNrGroup") as FormGroup;
        const ctrl = grp?.get("forbrugerNr");
        const existing = ctrl.validator ? [ctrl.validator] : [];
        ctrl.setValidators([...existing]);
        ctrl.updateValueAndValidity({ emitEvent: false });
        this.applyLoginDisabledState();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes["loginDisabled"] || changes["parent"]) {
            this.applyLoginDisabledState();
        }
    }

    private applyLoginDisabledState(): void {
        if (!this.parent) return;
        const controls = [
            this.parent.get("forbrugerNrGroup.forbrugerNr"),
            this.parent.get("forbrugerNrGroup.kode"),
            this.parent.get("emailGroup.email"),
            this.parent.get("emailGroup.adgangskode"),
            this.parent.get("huskMig")
        ];

        if (this.loginDisabled) {
            controls.forEach((control) => control?.disable({ emitEvent: false }));
            return;
        }

        controls.forEach((control) => control?.enable({ emitEvent: false }));
    }

    public focus(): void {
        if (this.loginDisabled) {
            return;
        }
        this.emailInput?.nativeElement?.focus();
    }

    public loginClick(): void {
        if (this.loginDisabled || this.arbejder) {
            return;
        }

        const loginData: LoginData = {
            brugernavn: this.parent.get("emailGroup.email").value,
            kodeord: this.parent.get("emailGroup.adgangskode").value,
            huskMig: this.parent.get("huskMig").value
        };

        if (this.technicianLogin) {
            this.onTechnicianLogin(loginData);
            return;
        }
        this.onForbrugerLogin(loginData);
        return;
    }

    private setArbejder(value: boolean): void {
        if (this.arbejder === value) {
            return;
        }
        this.arbejder = value;
        this.isLoading.emit(value);
        this.changeDetectorRef.markForCheck();
    }

    public onForbrugerLogin(credentials: LoginData): void {
        this.fejlBesked = "";
        this.setArbejder(true);
        this.loginService
            .login(credentials.brugernavn, credentials.kodeord, credentials.huskMig)
            .pipe(
                first(),
                switchMap((result) => from(this.processForbrugerLoginResult(result, credentials))),
                catchError((error: DffError) => {
                    this.matomoTracker.trackEvent("Authentication", "Login Failed", "Forbrugernummer - Error");
                    this.fejlBesked = error.friendlyMessage || error.message || "Login mislykkedes";
                    this.changeDetectorRef.markForCheck();
                    return EMPTY;
                }),
                finalize(() => {
                    this.setArbejder(false);
                })
            )
            .subscribe();
    }

    public onTechnicianLogin(credentials: LoginData): void {
        this.fejlBesked = "";
        this.setArbejder(true);
        this.loginService
            .loginAsTechnician(credentials.brugernavn, credentials.kodeord, credentials.huskMig)
            .pipe(
                first(),
                switchMap((result) => from(this.processTechnicianLoginResult(result, credentials))),
                catchError((error: DffError) => {
                    this.matomoTracker.trackEvent("Authentication", "Login Failed", `${this.teknikerLoginMatomoChannel} - Error`);
                    this.fejlBesked = error.friendlyMessage || error.message || "Login mislykkedes";
                    this.changeDetectorRef.markForCheck();
                    return EMPTY;
                }),
                finalize(() => {
                    this.setArbejder(false);
                })
            )
            .subscribe();
    }

    private async processForbrugerLoginResult(result: AuthenticationResult, credentials: LoginData): Promise<void> {
        try {
            if (result.success) {
                this.localStorage.setItem("sidste-login-side", "forbruger");
                await this.maybePromptBiometricLogin(credentials);
                this.matomoTracker.trackEvent("Authentication", "Login Success", "Forbrugernummer");
                this.emitLoginSucceeded();
                return;
            }

            this.matomoTracker.trackEvent("Authentication", "Login Failed", "Forbrugernummer - Invalid Credentials");
            if (this.parent.get("forbrugerNrGroup.forbrugerNr")?.hasError("digitsOrEmail")) {
                this.fejlBesked = "Forbrugernummer skal være enten kun tal eller en gyldig e-mail adresse";
            } else {
                this.fejlBesked = "Ugyldig forbrugernummer, e-mail eller kode";
            }
        } catch {
            this.fejlBesked = "Login mislykkedes";
        }
        this.changeDetectorRef.markForCheck();
    }

    private async processTechnicianLoginResult(result: AuthenticationResult, credentials: LoginData): Promise<void> {
        const channel = this.teknikerLoginMatomoChannel;
        try {
            if (result.success) {
                this.localStorage.setItem("sidste-login-side", "tekniker");
                await this.maybePromptBiometricLogin(credentials);
                this.matomoTracker.trackEvent("Authentication", "Login Success", channel);
                this.emitLoginSucceeded();
                return;
            }

            this.matomoTracker.trackEvent("Authentication", "Login Failed", `${channel} - Invalid Credentials`);
            this.fejlBesked = result.deniedRole
                ? "Brugeren har ikke tekniker-adgang på det valgte værk"
                : "Initialer eller e-mail var ikke gyldig";
        } catch {
            this.fejlBesked = "Login mislykkedes";
        }
        this.changeDetectorRef.markForCheck();
    }

    private async maybePromptBiometricLogin(credentials: LoginData): Promise<void> {
        const available = await this.biometricService.isAvailable();
        if (!available || localStorage.getItem("biometricAsked")) {
            return;
        }

        try {
            await this.biometricService.enableBiometricLogin(
                credentials.brugernavn,
                credentials.kodeord,
                "Aktiver biometrisk login",
                "Aktiver biometrisk login",
                "Bekræft din identitet for at gemme dine loginoplysninger sikkert. Herefter kan du logge ind hurtigere med finger- eller ansigtsgenkendelse."
            );
        } catch {
            // Brugeren afviste eller biometri fejlede – login fortsætter alligevel.
        } finally {
            localStorage.setItem("biometricAsked", "true");
        }
    }

    private emitLoginSucceeded(): void {
        setTimeout(() => this.loginSucceeded.emit());
    }

    private scheduleArbejderFalse(): void {
        setTimeout(() => {
            this.setArbejder(false);
        });
    }

    public loginHjaelpClick(): void {
        this.loginHjaelpService.visLoginHjaelpDialog();
    }

    public firstEmailLoginClick(): void {
        const email = this.parent.get("emailGroup.email").value;
        const adgangskode = this.parent.get("emailGroup.adgangskode").value;
        const forbrugerNr = this.parent.get("forbrugerNrGroup.forbrugerNr").value;
        const kode = this.parent.get("forbrugerNrGroup.kode").value;
        this.setArbejder(true);
        this.loginService
            .tilknytEmail(forbrugerNr, kode, email, adgangskode)
            .pipe(
                first(),
                tap(() => {
                    this.matomoTracker.trackEvent("Authentication", "Email Setup", "Success");
                    this.fejlBesked = "";
                    this.foersteGangAktiveret = false;
                }),
                catchError((error: any) => {
                    this.matomoTracker.trackEvent("Authentication", "Email Setup", "Failed");
                    this.fejlBesked = error.error;
                    return of(null);
                }),
                finalize(() => {
                    this.scheduleArbejderFalse();
                })
            )
            .subscribe();
    }

    public sendGlemtKodeordClick(): void {
        const email = this.parent.get("emailGroup.email").value;
        this.setArbejder(true);
        this.loginService
            .glemtKodeord(email)
            .pipe(
                first(),
                tap(() => {
                    this.matomoTracker.trackEvent("Authentication", "Forgot Password", "Success");
                    this.foersteGangAktiveret = false;
                    this.fejlBesked = "";
                    this.infoBesked = `E-mail sendt til: ${email}`;
                }),
                catchError((error: any) => {
                    this.matomoTracker.trackEvent("Authentication", "Forgot Password", "Failed");
                    this.matomoTracker.trackEvent("Authentication", "Forgot Password", "Failed");
                    this.fejlBesked = error.error;
                    return of(null);
                }),
                finalize(() => {
                    this.scheduleArbejderFalse();
                })
            )
            .subscribe();
    }

    public onGlemtKodeordClick(): void {
        this.glemtKodeordAktiveret = !this.glemtKodeordAktiveret;
        this.fejlBesked = "";
        this.foersteGangAktiveret = false;
        this.infoBesked = "";
    }

    public firstTimeClick(): void {
        this.fejlBesked = "";
        this.foersteGangAktiveret = !this.foersteGangAktiveret;
    }

    public redirectEfterLogin(): void {
        const path = this.routeParams.get("path");
        if (path && path !== "404") {
            this.redirectService.redirect(path);
        } else {
            this.redirectService.redirectTilInstallationer();
        }
    }
}
